import { Box, Copy, Icon, IconWrapper, Link, Optional } from '@honda-canada/design-system-react';
import React, { useContext, useState } from 'react';
import flatten from 'lodash/flatten';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { selectBodyTypesForGtm, selectCurrentVehicleFromVehicles } from '../../../core/reducers/vehicles';
import ColorSwatchCarousel from './ColorSwatchCarousel';
import CommonModal from '../../CommonModal/CommonModal';
import Context from '../service/Context';
import { EqualHeight } from '../../../utils/components/EqualHeight';
import ProductCard from './ProductCard';
import { getOtherColors } from '../utils/utils';
import gtmConfigurationChangeConfirmOrCancel from '../../../core/gtm/gtmConfigurationChangeConfirmOrCancel';
import gtmExteriorColorsAvailableClick from '../../../core/gtm/gtmExteriorColorsAvailableClick';
import gtmToggleAvailableColorsClick from '../../../core/gtm/gtmToggleAvailableColorsClick';
import { removeAllTagsFromMarkdown } from '../../../utils/markdown';
import { selectCurrentVehicle } from '../../../core/reducers/currentVehicle';
import themeStyles from './OtherTrimColours.styles';
import { useAppSelector } from '../../../core/utils';
import useModelData from '../../TrimCardGrid/hooks/useModelData';
const MainContainer = themeStyles.apply(Box, 'MainContainer');
const TitleContainer = themeStyles.apply(Box, 'TitleContainer');
const Title = themeStyles.apply(Link, 'Title');
const ModalCardsWrapper = themeStyles.apply(Box, 'ModalCardsWrapper');
const ModalContentWrapper = themeStyles.apply(Box, 'ModalContentWrapper');
const OtherTrimColours = ({ componentName }) => {
    var _a, _b;
    const history = useHistory();
    const { t } = useTranslation();
    const [showModal, setShowModal] = useState(false);
    const [otherTrimsSelectedColorKey, setOtherTrimsSelectedColorKey] = useState('');
    const [selectedTrim, setSelectedTrim] = useState(undefined);
    const [otherTrimsToShow, setOtherTrimsToShow] = useState([]);
    const [isVisible, setIsVisible] = useState(false);
    const { iconWrapperMarginTop } = useContext(Context);
    const gtmBodyStyle = useAppSelector(selectBodyTypesForGtm);
    const { transmissionKey, exteriorColorKey, modelYear, inventoryStatus } = useAppSelector(selectCurrentVehicle);
    const currentVehicle = useAppSelector(selectCurrentVehicleFromVehicles);
    const { trims } = useModelData((_a = currentVehicle === null || currentVehicle === void 0 ? void 0 : currentVehicle.modelSitecoreId) !== null && _a !== void 0 ? _a : '', modelYear || '');
    const allTransmissions = flatten(trims.map(trim => trim.transmissions));
    const otherColors = getOtherColors({ allTransmissions, transmissionKey, exteriorColorKey });
    const otherColorsSwatches = otherColors === null || otherColors === void 0 ? void 0 : otherColors.map(color => ({
        color: (color === null || color === void 0 ? void 0 : color.hexValue) || '',
        key: (color === null || color === void 0 ? void 0 : color.key) || '',
        name: (color === null || color === void 0 ? void 0 : color.colorName) || '',
    }));
    let selectedColorName = ((_b = otherColorsSwatches === null || otherColorsSwatches === void 0 ? void 0 : otherColorsSwatches.find(color => (color === null || color === void 0 ? void 0 : color.key) === otherTrimsSelectedColorKey)) === null || _b === void 0 ? void 0 : _b.name) || '';
    selectedColorName = removeAllTagsFromMarkdown(selectedColorName) || '';
    const gtmPayload = {
        gtmBodyStyle,
        availabilityFlag: inventoryStatus,
        trim: currentVehicle === null || currentVehicle === void 0 ? void 0 : currentVehicle.trimGtmName,
        model: currentVehicle === null || currentVehicle === void 0 ? void 0 : currentVehicle.modelGtmName,
    };
    const handleClick = () => {
        setIsVisible((prop) => !prop);
        gtmToggleAvailableColorsClick({
            ...gtmPayload,
            componentType: componentName,
        });
    };
    const handleSelectTrim = (trim) => setSelectedTrim(trim);
    const handleCloseModal = () => {
        gtmConfigurationChangeConfirmOrCancel({
            ...gtmPayload,
            action: 'cancel',
        });
        setShowModal(false);
    };
    const handleShowModal = (key) => {
        const currentColor = otherColorsSwatches === null || otherColorsSwatches === void 0 ? void 0 : otherColorsSwatches.find(color => color.key === key);
        const exteriorColorName = currentColor === null || currentColor === void 0 ? void 0 : currentColor.name;
        gtmExteriorColorsAvailableClick({
            ...gtmPayload,
            colour: exteriorColorName,
        });
        setSelectedTrim(undefined);
        setShowModal(true);
        setOtherTrimsSelectedColorKey(key);
        const availableTrims = trims.filter(trim => trim.transmissions.some(transmission => { var _a; return (_a = transmission === null || transmission === void 0 ? void 0 : transmission.exteriorColors) === null || _a === void 0 ? void 0 : _a.some(color => color.key === key); }));
        setOtherTrimsToShow(availableTrims);
        if (availableTrims && availableTrims.length === 1)
            setSelectedTrim(availableTrims[0]);
    };
    const handleSubmit = () => {
        var _a;
        gtmConfigurationChangeConfirmOrCancel({
            ...gtmPayload,
            action: 'confirm',
        });
        setShowModal(false);
        if (!selectedTrim) {
            setShowModal(false);
            return;
        }
        const searchParams = new URLSearchParams({
            modelYear: selectedTrim.modelYear,
            modelKey: selectedTrim.modelKey,
            trimKey: selectedTrim.trimKey,
            transmissionKey: selectedTrim.defaultTransmission,
            exteriorColorKey: otherTrimsSelectedColorKey,
            interiorColorKey: ((_a = selectedTrim === null || selectedTrim === void 0 ? void 0 : selectedTrim.transmissions[0]) === null || _a === void 0 ? void 0 : _a.interiorColorKey) || '',
        });
        history.push({ search: searchParams.toString() });
        setShowModal(false);
    };
    const title = isVisible ? t('Pages.Bap.hideOtherTrimColoursLabel') : t('Pages.Bap.showOtherTrimColoursLabel');
    if (!otherColorsSwatches.length)
        return null;
    return (React.createElement(MainContainer, null,
        React.createElement(TitleContainer, { onClick: handleClick },
            React.createElement(IconWrapper, { padding: "zero", marginTop: iconWrapperMarginTop, backgroundColor: "transparent", as: "button", cursor: "pointer", border: "none", "data-testid": isVisible ? 'cy-other-trim-colours-visibility-on' : 'cy-other-trim-colours-visibility-off', "aria-label": title },
                React.createElement(Icon, { iconColor: "blue", name: isVisible ? 'visibilityStatusOff' : 'visibilityStatusOn' })),
            React.createElement(Title, { as: "button", styling: "tertiary", "data-testid": "cy-other-trim-colours-label" }, title)),
        React.createElement(Optional, { when: isVisible },
            React.createElement(ColorSwatchCarousel, { swatches: otherColorsSwatches, handleClick: handleShowModal })),
        React.createElement(CommonModal, { handleCloseModal: handleCloseModal, handleSubmit: handleSubmit, showModal: showModal, title: t('Pages.Bap.configChangeHeading'), submitButtonEnabled: Boolean(selectedTrim === null || selectedTrim === void 0 ? void 0 : selectedTrim.trimKey), primaryButtonText: t('Pages.Bap.okButton') },
            React.createElement(ModalContentWrapper, { "data-body-scroll-lock-ignore": true },
                React.createElement(Copy, { size: "regular", "data-testid": "cy-CommonModal-subtitle", pb: "xs" }, t('Pages.Bap.colourModalTopText', { extColourName: selectedColorName })),
                React.createElement(ModalCardsWrapper, { "data-testid": "cy-modal-container" },
                    React.createElement(EqualHeight, null, otherTrimsToShow.map(trim => (React.createElement(ProductCard, { key: trim.trimKey, product: trim, handleSelectTrim: handleSelectTrim, isSelected: trim.trimKey === (selectedTrim === null || selectedTrim === void 0 ? void 0 : selectedTrim.trimKey) && otherTrimsToShow.length > 1 })))))),
            React.createElement(Copy, { size: "regular", "data-testid": "cy-CommonModal-bottom-text" }, t('Pages.Bap.colourModalBottomText')))));
};
export default OtherTrimColours;
